import { Suspense, useEffect, useRef, useState } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import Logo from './Logo';
import { useAuth } from '../context/AuthContext';
import { ShellTopbarContext } from '../context/ShellTopbarContext';
import { roleLabel } from '../lib/format';

/** Shown only while a lazy-loaded page chunk is being fetched. */
function PageLoading() {
  return (
    <div className="route-loading" style={{ padding: 40, textAlign: 'center' }}>
      <span className="material-symbols-outlined" aria-hidden="true">hourglass_top</span>
      <span className="page-subtitle">Loading…</span>
    </div>
  );
}

/**
 * The shared application shell.
 *
 * A direct port of the markup every signed-in page in the original app repeated:
 * `.app` > `.sidebar` + `.main` > `.topbar` + `.content`.
 *
 * The sidebar sits on the navy background, so the mark here is the WHITE logo.
 */

const NAV = {
  // Icons and labels are taken verbatim from the original sidebars
  // (client/dashboard.html, manager/workspace.html, agent/workspace.html,
  // admin/dashboard.html) so the navigation reads identically. Daily Reports
  // sits second in every role's list: it is the app's core daily traffic.
  client: [
    { to: '/client', end: true, icon: 'space_dashboard', label: 'My Projects' },
    { to: '/reports/client', icon: 'summarize', label: 'Reports' },
    { to: '/client/notifications', icon: 'notifications', label: 'Notifications' },
    { to: '/client/new-project', icon: 'add_box', label: 'New Project' },
    { to: '/client/profile', icon: 'person', label: 'Profile' },
  ],  project_manager: [
    { to: '/manager', end: true, activeView: 'dashboard', icon: 'space_dashboard', label: 'Dashboard' },
    // The daily recording cycle leads: reports and photos are what keep the
    // client's view of the project honest, so the manager reaches them first.
    { to: '/manager', view: 'reports', icon: 'edit_calendar', label: 'Daily Reports' },
    { to: '/manager', view: 'photos', icon: 'photo_camera', label: 'Photos' },
    { to: '/manager', view: 'centre', icon: 'verified', label: 'Verification Centre' },
    { to: '/manager', view: 'labour', icon: 'groups', label: 'Labour Register' },
    { to: '/manager', view: 'deliveries', icon: 'local_shipping', label: 'Material Verification' },
    { to: '/manager', view: 'usage', icon: 'inventory_2', label: 'Material Usage' },
    { to: '/manager', view: 'equipment', icon: 'engineering', label: 'Plant & Equipment' },
    { to: '/manager', view: 'quality', icon: 'fact_check', label: 'Quality Verification' },
    // One-off project work follows: setup, the plan, then management views.
    { to: '/manager', view: 'setup', icon: 'checklist', label: 'Project Setup' },
    { to: '/manager', view: 'milestones', icon: 'flag', label: 'Milestones' },
    { to: '/manager', view: 'progress', icon: 'trending_up', label: 'Project Progress' },
    { to: '/manager', view: 'financial', icon: 'account_balance', label: 'Financial Management' },
    { to: '/manager', view: 'analytics', icon: 'monitoring', label: 'Analytics' },
    { to: '/manager', view: 'documents', icon: 'folder', label: 'Documents' },
    { to: '/manager', view: 'timeline', icon: 'timeline', label: 'Project Timeline' },
    { to: '/manager', view: 'agents', icon: 'badge', label: 'Site Agents' },
  ],
  site_agent: [
    { to: '/agent', end: true, activeView: 'dashboard', icon: 'space_dashboard', label: 'Dashboard' },
    // Same principle: what the agent records every day comes first.
    { to: '/agent', view: 'reports', icon: 'edit_calendar', label: 'Daily Reports' },
    { to: '/agent', view: 'photos', icon: 'photo_camera', label: 'Photos' },
    { to: '/agent', view: 'labour', icon: 'groups', label: 'Labour Register' },
    { to: '/agent', view: 'deliveries', icon: 'verified', label: 'Material Verification' },
    { to: '/agent', view: 'usage', icon: 'inventory_2', label: 'Material Usage' },
    { to: '/agent', view: 'equipment', icon: 'engineering', label: 'Plant & Equipment' },
    { to: '/agent', view: 'quality', icon: 'fact_check', label: 'Quality Checklist' },
    { to: '/agent', view: 'documents', icon: 'folder', label: 'Documents' },
    { to: '/agent', view: 'timeline', icon: 'timeline', label: 'Project Timeline' },
  ],
  administrator: [
    { to: '/admin', end: true, icon: 'space_dashboard', label: 'Dashboard' },
    { to: '/admin/users', icon: 'group', label: 'Users' },
    { to: '/admin/projects', icon: 'folder', label: 'Projects' },
    { to: '/reports/admin', icon: 'summarize', label: 'Reports' },
    { to: '/admin/users', filter: 'Pending', icon: 'verified_user', label: 'Account Approvals' },
    { to: '/admin/profile', icon: 'person', label: 'Profile' },
  ],
};

/**
 * The twelve project sections, exactly the tabs on client/project.html. Once
 * a client enters a project, the sidebar becomes THIS list: the project is
 * the dashboard, each section is a page, and the account-level items step
 * aside for the first item (back to My Projects).
 */
/**
 * The section a role lands on when the URL carries no ?view=. Only the roles
 * whose sections all share one path need this.
 */
const DEFAULT_VIEW = {
  project_manager: 'dashboard',
  site_agent: 'dashboard',
};

const PROJECT_SECTIONS = [
  { key: 'overview', label: 'Overview', icon: 'home' },
  // Daily reports and photos are the client's proof of work: the transparency
  // pair the whole product exists for, so they come straight after Overview.
  { key: 'reports', label: 'Daily Reports', icon: 'article' },
  { key: 'photos', label: 'Photos', icon: 'photo_camera' },
  { key: 'finances', label: 'Finances', icon: 'account_balance' },
  { key: 'documents', label: 'Documents', icon: 'folder_open' },
  { key: 'progress', label: 'Progress Tracker', icon: 'engineering' },
  { key: 'timeline', label: 'Timeline', icon: 'history' },
  { key: 'materials', label: 'Materials', icon: 'inventory_2' },
  { key: 'labour', label: 'Labour', icon: 'groups' },
  { key: 'plant', label: 'Plant & Equipment', icon: 'construction' },
  { key: 'quality', label: 'Quality Verification', icon: 'fact_check' },
];

export default function AppShell({ children, notice }) {
  const { profile, role, signOut } = useAuth();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [topbarContent, setTopbarContent] = useState(null);
  const menuRef = useRef(null);
  let links = NAV[role] ?? [];

  // Manager sections are distinguished by ?view=; the path alone is identical
  // for all of them, so the query string has to be carried on the link.
  const location = useLocation();
  const currentParams = new URLSearchParams(location.search);
  // A role whose sections all live under one path has a default view, so the
  // right entry is marked even when the URL carries no ?view=.
  const currentView = currentParams.get('view') ?? DEFAULT_VIEW[role] ?? null;

  // Inside a client project the whole shell changes context: the sidebar
  // lists the project's own sections and the only way out is back to the
  // project list. No other account-level nav inside a project.
  const inProject = location.pathname.match(/^\/client\/project\/([^/]+)/);
  if (inProject) {
    const base = `/client/project/${inProject[1]}`;
    links = [
      { to: '/client', end: true, icon: 'arrow_back', label: 'My Projects' },
      ...PROJECT_SECTIONS.map((s) => ({ to: `${base}/${s.key}`, icon: s.icon, label: s.label })),
    ];
  }

  function targetFor(l) {
    if (!l.view && !l.filter) return l.to;
    const p = new URLSearchParams(location.search);
    if (l.view) p.set('view', l.view);
    if (l.filter) p.set('filter', l.filter);
    return `${l.to}?${p.toString()}`;
  }

  /**
   * Whether a nav entry is the section the user is on.
   *
   * Sections that share one path (the manager and agent workspaces) are
   * told apart by ?view=, so the entry is marked when the view in the URL
   * is its own. The top link of such a section carries `activeView` for the
   * same reason: it must not stay marked while another section is open.
   */
  function isCurrent(l, isActive) {
    if (l.activeView) return currentView === l.activeView;
    if (l.view) return isActive && currentView === l.view;
    return isActive;
  }

  async function handleLogout() {
    await signOut();
    navigate('/');
  }

  useEffect(() => {
    function onDocClick() { setMenuOpen(false); }
    document.addEventListener('click', onDocClick);
    function onResize() {
      if (window.innerWidth > 860) setMobileOpen(false);
    }
    window.addEventListener('resize', onResize);
    return () => {
      document.removeEventListener('click', onDocClick);
      window.removeEventListener('resize', onResize);
    };
  }, []);

  // The original locks body scroll while the drawer is open, so the page
  // cannot be scrolled out from under it.
  useEffect(() => {
    document.body.classList.toggle('drawer-locked', mobileOpen);
    document.documentElement.classList.toggle('drawer-locked', mobileOpen);
    return () => {
      document.body.classList.remove('drawer-locked');
      document.documentElement.classList.remove('drawer-locked');
    };
  }, [mobileOpen]);

  // Escape closes the drawer.
  useEffect(() => {
    if (!mobileOpen) return undefined;
    function onKey(e) { if (e.key === 'Escape') setMobileOpen(false); }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [mobileOpen]);

  const displayName = profile?.full_name || roleLabel(role);
  const initials = displayName.split(/\s+/).filter(Boolean).slice(0, 2).map((p) => p.charAt(0)).join('').toUpperCase() || '?';
  const today = new Date().toLocaleDateString(undefined, {
    weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
  });
  const year = new Date().getFullYear();

  return (
    <ShellTopbarContext.Provider value={{ setTopbarContent }}>
      <div className={`app${collapsed ? ' sidebar-collapsed' : ''}`}>
      <aside className={`sidebar${collapsed ? ' collapsed' : ''}${mobileOpen ? ' open' : ''}`} id="sidebar">
        <div className="sidebar-brand">
          <Logo variant="white" className="brand-logo" height={56} alt="Valdon logo" />
          {/* Shown only at the mobile breakpoint; see .sidebar-close in CSS. */}
          <button
            className="sidebar-close"
            type="button"
            aria-label="Close menu"
            onClick={() => setMobileOpen(false)}
          >
            <span className="material-symbols-outlined" aria-hidden="true">close</span>
          </button>
        </div>

        <nav className="sidebar-nav">
          {links.map((l) => (
            <NavLink
              key={`${l.to}-${l.view ?? ''}-${l.label}`}
              to={targetFor(l)}
              end={l.end}
              // Manager sections differ only by ?view=, so they must be
              // compared on the query string rather than the path alone.
              className={({ isActive }) => `nav-link${isCurrent(l, isActive) ? ' active' : ''}`}
              onClick={() => setMobileOpen(false)}
            >
              <span className="material-symbols-outlined" aria-hidden="true">{l.icon}</span>
              <span className="nav-label">{l.label}</span>
            </NavLink>
          ))}
        </nav>

        <div className="sidebar-footer">
          <button
            id="sidebar-collapse"
            className="sidebar-collapse"
            type="button"
            onClick={() => setCollapsed((c) => !c)}
          >
            <span className="material-symbols-outlined" aria-hidden="true">
              {collapsed ? 'chevron_right' : 'chevron_left'}
            </span>
            <span className="collapse-label">Collapse Sidebar</span>
          </button>
          <button id="sidebar-logout" className="logout-button" type="button" onClick={handleLogout}>
            <span className="material-symbols-outlined" aria-hidden="true">logout</span>
            <span className="logout-label">Logout</span>
          </button>
        </div>
      </aside>

      <div className="main">
        <header className="topbar">
          <button
            id="menu-toggle"
            className="menu-toggle"
            type="button"
            aria-label="Toggle menu"
            onClick={() => setMobileOpen((o) => !o)}
          >
            <span className="material-symbols-outlined" aria-hidden="true">menu</span>
          </button>

          {/* Pages that need a control in the top bar (the original agent
              workspace put its project switcher here) publish it through
              ShellTopbarContext. */}
          <div className="topbar-greeting">{topbarContent}</div>

          <div className="topbar-right">
            <span className="topbar-date">{today}</span>
            <div className="user-menu" ref={menuRef}>
              <button
                className="user-menu-btn"
                type="button"
                aria-label="Account menu"
                title={displayName}
                onClick={(e) => { e.stopPropagation(); setMenuOpen((o) => !o); }}
              >
                <span className="user-avatar">{initials}</span>
                <span className="user-menu-name">{displayName}</span>
                <span className="material-symbols-outlined" aria-hidden="true">expand_more</span>
              </button>
              <div className="user-dropdown" hidden={!menuOpen}>
                <div className="user-dropdown-head">
                  <div className="user-dropdown-name">{displayName}</div>
                  <div className="user-dropdown-email">{profile?.email ?? ''}</div>
                </div>
                <button className="user-dropdown-item danger user-menu-logout" type="button" onClick={handleLogout}>
                  <span className="material-symbols-outlined" aria-hidden="true">logout</span> Sign out
                </button>
              </div>
            </div>
          </div>
        </header>

        <main className="content">
          <div id="error-banner" className="error-banner" hidden></div>
          {notice}
          <Suspense fallback={<PageLoading />}>{children}</Suspense>
        </main>

        <footer className="app-footer">
          <div className="app-footer-inner">
            <span>&copy; {year} Valdon Building &amp; Construction Services · Version 1.2</span>
          </div>
        </footer>
      </div>

      {/* Tap-anywhere-outside closes the drawer, as in the original js/ui.js. */}
      {mobileOpen && (
        <div className="sidebar-backdrop" onClick={() => setMobileOpen(false)} />
      )}
      </div>
    </ShellTopbarContext.Provider>
  );
}